import { FormEvent, ReactNode, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Alert,
  Button,
  Details,
  Dialog,
  Field,
  Heading,
  Input,
  Label,
  Paragraph,
  Select,
  Tag,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { TrashIcon } from '@navikt/aksel-icons';
import { useGetDistributionChannelsQuery } from '../../hooks';
import { useInitiateTeamsOAuth } from '../../hooks/mutations/useInitiateTeamsOAuth';
import { useInitiateSlackOAuth } from '../../hooks/mutations/useInitiateSlackOAuth';
import { useInstallSlackChannel } from '../../hooks/mutations/useInstallSlackChannel';
import { useGetSlackWorkspaces } from '../../hooks/queries/useGetSlackWorkspaces';
import { useCreateNewDistributionChannelMutation } from '../../hooks/mutations/useCreateNewDistributionChannelMutation';
import { useDeleteDistributionChannelMutation } from '../../hooks/mutations/useDeleteDistributionChannelMutation';
import { DistributionChannel } from '../../api/queries';
import { GhostLoader } from '../GhostLoader';
import { SettingsSection } from '../TeamSettingsLayout';
import { SettingsListItem } from '../TeamSettingsLayout/SettingsListItem';
import classes from './TeamIntegrationCards.module.css';

const TOAST_POSITION = 'bottom-right' as const;
const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;
const PHONE_PATTERN = /^\+?\d{8,15}$/;

type WebhookType = 'Slack' | 'Microsoft Teams';

/** Which personal channel the add dialog is for */
type PersonalDialog = { kind: 'email' | 'sms'; value: string; error: string | null } | null;

const displayValue = (channel: DistributionChannel) => {
  const isTeams = channel.type === 'Microsoft Teams' || (channel.type as string) === 'MicrosoftTeams';
  if (isTeams && channel.teamsChannelName) return `#${channel.teamsChannelName}`;
  if (channel.type === 'Slack' && channel.slackChannelName) return `#${channel.slackChannelName}`;
  return channel.webhookUrl || '';
};

type ChannelCardProps = {
  title: string;
  description: string;
  children?: ReactNode;
  action: ReactNode;
};

const ChannelCard = ({ title, description, children, action }: ChannelCardProps) => (
  <li className={classes.card}>
    <Heading level={3} data-size="2xs">
      {title}
    </Heading>
    <Paragraph data-size="sm" className={classes.subtle}>
      {description}
    </Paragraph>
    <div className={classes.cardContent}>{children}</div>
    <div>{action}</div>
  </li>
);

export const TeamIntegrationCards = () => {
  const { t } = useTranslation();
  const { teamId } = useParams();
  const { data: distributionChannels = [], isLoading, error } = useGetDistributionChannelsQuery(
    Number(teamId),
  );

  const [channelToDelete, setChannelToDelete] = useState<DistributionChannel | null>(null);
  const [personalDialog, setPersonalDialog] = useState<PersonalDialog>(null);
  const [isSlackDialogOpen, setIsSlackDialogOpen] = useState(false);
  const [selectedSlackWorkspace, setSelectedSlackWorkspace] = useState<number | null>(null);
  const [selectedSlackChannel, setSelectedSlackChannel] = useState('');
  const [isWebhookDialogOpen, setIsWebhookDialogOpen] = useState(false);
  const [webhookType, setWebhookType] = useState<WebhookType>('Slack');
  const [webhookValue, setWebhookValue] = useState('');

  const { mutate: initiateTeamsOAuth, isPending: isInitiatingTeamsOAuth } = useInitiateTeamsOAuth();
  const { mutate: initiateSlackOAuth, isPending: isInitiatingSlackOAuth } = useInitiateSlackOAuth();
  const { mutate: installSlackChannel, isPending: isInstallingSlackChannel } =
    useInstallSlackChannel();
  const { data: slackWorkspacesData, isLoading: isLoadingSlackWorkspaces } = useGetSlackWorkspaces();
  const { mutate: createNewDistributionChannel, isPending: isCreating } =
    useCreateNewDistributionChannelMutation(Number(teamId));
  const { mutate: deleteDistributionChannel, isPending: isDeleting } =
    useDeleteDistributionChannelMutation(Number(teamId));

  const workspaces = slackWorkspacesData?.workspaces ?? [];
  const hasSlackWorkspaces = !isLoadingSlackWorkspaces && workspaces.length > 0;

  const handleTeamsConnect = () => {
    const parsedTeamId = Number(teamId);
    if (!teamId || Number.isNaN(parsedTeamId)) {
      toast.error(t('integrations.microsoftTeams.errorMissingTeamId'), { position: TOAST_POSITION });
      return;
    }

    initiateTeamsOAuth(
      { teamId: parsedTeamId },
      {
        onSuccess: (data) => {
          sessionStorage.setItem('teams_oauth_teamId', teamId || '');
          sessionStorage.setItem('teams_oauth_context', 'settings');
          window.location.href = data.authorizationUrl;
        },
        onError: () => {
          toast.error(t('integrations.microsoftTeams.errorConnectFailed'), { position: TOAST_POSITION });
        },
      },
    );
  };

  const handleSlackConnect = () => {
    initiateSlackOAuth(undefined, {
      onSuccess: (data) => {
        sessionStorage.setItem('slack_oauth_teamId', teamId || '');
        sessionStorage.setItem('slack_oauth_context', 'settings');
        window.location.href = data.authorizationUrl;
      },
      onError: () => {
        toast.error(t('integrations.slack.errorConnectFailed'), { position: TOAST_POSITION });
      },
    });
  };

  const createChannel = (
    type: DistributionChannel['type'],
    webhookUrl: string,
    onSuccess: () => void,
    errorMessage: string,
  ) => {
    createNewDistributionChannel(
      { type, webhookUrl },
      {
        onSuccess,
        onError: () => toast.error(errorMessage, { position: TOAST_POSITION }),
      },
    );
  };

  const handleAddPersonal = (event: FormEvent) => {
    event.preventDefault();
    if (!personalDialog) return;
    const isEmail = personalDialog.kind === 'email';
    const value = isEmail ? personalDialog.value.trim() : personalDialog.value.replace(/\s/g, '');
    const type = (isEmail ? 'E-post' : 'SMS') as DistributionChannel['type'];

    const isValid = isEmail ? EMAIL_PATTERN.test(value) : PHONE_PATTERN.test(value);
    if (!isValid) {
      setPersonalDialog({
        ...personalDialog,
        error: t(isEmail ? 'teamSettings.channels.emailInvalid' : 'teamSettings.channels.smsInvalid'),
      });
      return;
    }
    const isDuplicate = distributionChannels.some(
      (channel) => channel.type === type && channel.webhookUrl?.toLowerCase() === value.toLowerCase(),
    );
    if (isDuplicate) {
      setPersonalDialog({ ...personalDialog, error: t('teamSettings.channels.duplicate') });
      return;
    }

    createChannel(
      type,
      value,
      () => {
        setPersonalDialog(null);
        toast.success(t(isEmail ? 'integrations.email.success' : 'integrations.sms.success'), {
          position: TOAST_POSITION,
        });
      },
      t(isEmail ? 'integrations.email.error' : 'integrations.sms.error'),
    );
  };

  const closeSlackDialog = () => {
    setIsSlackDialogOpen(false);
    setSelectedSlackChannel('');
    setSelectedSlackWorkspace(null);
  };

  const handleAddSlackChannel = () => {
    if (!selectedSlackChannel || !selectedSlackWorkspace || !teamId) return;

    installSlackChannel(
      {
        trustpulseTeamId: Number(teamId),
        slackChannelId: selectedSlackChannel,
        slackWorkspaceId: selectedSlackWorkspace,
      },
      {
        onSuccess: (data) => {
          closeSlackDialog();
          toast.success(t('integrations.slack.successChannelAdded', { channelName: data.channelName }), {
            position: TOAST_POSITION,
          });
          window.location.reload();
        },
        onError: () => {
          toast.error(t('integrations.slack.errorAddChannelFailed'), { position: TOAST_POSITION });
        },
      },
    );
  };

  const handleAddWebhook = (event: FormEvent) => {
    event.preventDefault();
    if (!webhookValue.trim()) return;
    createChannel(
      webhookType as DistributionChannel['type'],
      webhookValue.trim(),
      () => {
        setIsWebhookDialogOpen(false);
        setWebhookValue('');
        toast.success(t('integrations.advanced.webhookSuccess'), { position: TOAST_POSITION });
      },
      t('integrations.advanced.webhookError'),
    );
  };

  const onDeleteDistributionChannel = () => {
    if (!channelToDelete) return;
    deleteDistributionChannel(channelToDelete.id, {
      onSuccess: () => {
        toast.success(t('integrations.common.channelDeleted'), { position: TOAST_POSITION });
      },
    });
    setChannelToDelete(null);
  };

  if (isLoading) {
    return <GhostLoader style={{ height: 345, width: '100%' }} />;
  }

  if (error) {
    return (
      <Alert data-color="danger">
        <Paragraph>{error.message}</Paragraph>
      </Alert>
    );
  }

  const channelsOf = (...types: string[]) =>
    distributionChannels.filter((channel) => types.includes(channel.type));

  const channelList = (channels: DistributionChannel[], emptyText: string) =>
    channels.length > 0 ? (
      <ul className={classes.channelList}>
        {channels.map((channel) => {
          const value = displayValue(channel);
          return (
            <SettingsListItem
              key={channel.id}
              initial={channel.type.charAt(0).toUpperCase()}
              title={value}
              action={
                <Button
                  variant="tertiary"
                  data-color="danger"
                  data-size="sm"
                  aria-label={t('teamSettings.channels.removeLabel', { type: channel.type, value })}
                  onClick={() => setChannelToDelete(channel)}
                >
                  <TrashIcon aria-hidden="true" />
                  {t('teamSettings.common.remove')}
                </Button>
              }
            />
          );
        })}
      </ul>
    ) : (
      <Paragraph data-size="sm" className={classes.subtle}>
        {emptyText}
      </Paragraph>
    );

  const slackChannelOptions = (() => {
    const workspace = workspaces.find((w) => w.id === selectedSlackWorkspace);
    if (!workspace) return [];
    return [
      ...workspace.channels
        .filter((c) => !c.isPrivate)
        .map((channel) => ({ label: `#${channel.name}`, value: channel.id })),
      ...workspace.channels
        .filter((c) => c.isPrivate)
        .map((channel) => ({ label: `🔒 ${channel.name}`, value: channel.id })),
    ];
  })();

  const isEmailDialog = personalDialog?.kind === 'email';

  return (
    <>
      <SettingsSection
        title={t('integrations.sections.sharedChannels')}
        description={t('teamSettings.channels.sharedDescription')}
      >
        <ul className={classes.grid}>
          <ChannelCard
            title={t('integrations.microsoftTeams.title')}
            description={t('teamSettings.channels.teamsDescription')}
            action={
              <Button
                variant="secondary"
                data-size="sm"
                onClick={handleTeamsConnect}
                disabled={isInitiatingTeamsOAuth}
              >
                {t('integrations.microsoftTeams.connectButton')}
              </Button>
            }
          >
            {channelList(channelsOf('Microsoft Teams', 'MicrosoftTeams'), t('teamSettings.channels.noChannel'))}
          </ChannelCard>

          <ChannelCard
            title={t('integrations.slack.title')}
            description={t('teamSettings.channels.slackDescription')}
            action={
              hasSlackWorkspaces ? (
                <Button variant="secondary" data-size="sm" onClick={() => setIsSlackDialogOpen(true)}>
                  {t('integrations.slack.addChannelButton')}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  data-size="sm"
                  onClick={handleSlackConnect}
                  disabled={isInitiatingSlackOAuth || isLoadingSlackWorkspaces}
                >
                  {t('integrations.slack.connectButton')}
                </Button>
              )
            }
          >
            {hasSlackWorkspaces && (
              <Paragraph data-size="sm" className={classes.connected}>
                <Tag data-size="sm" data-color="success">
                  {t('teamSettings.channels.connected')}
                </Tag>
                {workspaces.map((workspace) => workspace.workspaceName).join(', ')}
              </Paragraph>
            )}
            {channelList(channelsOf('Slack'), t('teamSettings.channels.noChannel'))}
          </ChannelCard>
        </ul>
      </SettingsSection>

      <SettingsSection
        title={t('integrations.sections.personalMessages')}
        description={t('teamSettings.channels.personalDescription')}
      >
        <ul className={classes.grid}>
          <ChannelCard
            title={t('integrations.email.title')}
            description={t('teamSettings.channels.emailDescription')}
            action={
              <Button
                variant="secondary"
                data-size="sm"
                onClick={() => setPersonalDialog({ kind: 'email', value: '', error: null })}
              >
                {t('integrations.email.addButton')}
              </Button>
            }
          >
            {channelList(channelsOf('E-post'), t('teamSettings.channels.noEmail'))}
          </ChannelCard>

          <ChannelCard
            title={t('integrations.sms.title')}
            description={t('teamSettings.channels.smsDescription')}
            action={
              <Button
                variant="secondary"
                data-size="sm"
                onClick={() => setPersonalDialog({ kind: 'sms', value: '', error: null })}
              >
                {t('teamSettings.channels.addSms')}
              </Button>
            }
          >
            {channelList(channelsOf('SMS'), t('teamSettings.channels.noSms'))}
          </ChannelCard>
        </ul>

        <Details>
          <Details.Summary>{t('teamSettings.channels.advancedTitle')}</Details.Summary>
          <Details.Content>
            <div className={classes.advanced}>
              <Paragraph data-size="sm">{t('teamSettings.channels.advancedDescription')}</Paragraph>
              <div>
                <Button variant="secondary" data-size="sm" onClick={() => setIsWebhookDialogOpen(true)}>
                  {t('teamSettings.channels.addWebhook')}
                </Button>
              </div>
            </div>
          </Details.Content>
        </Details>
      </SettingsSection>

      <Dialog
        open={personalDialog !== null}
        onClose={() => setPersonalDialog(null)}
        closedby="any"
        closeButton={false}
        aria-labelledby="personal-channel-heading"
        className={classes.dialog}
      >
        <form className={classes.dialogContent} onSubmit={handleAddPersonal} noValidate>
          <Heading id="personal-channel-heading" level={2} data-size="xs">
            {isEmailDialog ? t('integrations.email.dialogTitle') : t('teamSettings.channels.addSms')}
          </Heading>
          <Field>
            <Label htmlFor="personal-channel-value">
              {isEmailDialog ? t('integrations.email.label') : t('teamSettings.channels.smsDialogLabel')}
            </Label>
            <Paragraph data-size="sm" id="personal-channel-description" className={classes.subtle}>
              {isEmailDialog
                ? t('teamSettings.channels.emailDialogDescription')
                : t('teamSettings.channels.smsDialogDescription')}
            </Paragraph>
            <Input
              id="personal-channel-value"
              type={isEmailDialog ? 'email' : 'tel'}
              inputMode={isEmailDialog ? 'email' : 'tel'}
              aria-describedby="personal-channel-description"
              aria-invalid={personalDialog?.error ? true : undefined}
              value={personalDialog?.value ?? ''}
              onChange={(e) =>
                personalDialog && setPersonalDialog({ ...personalDialog, value: e.target.value, error: null })
              }
            />
            {personalDialog?.error && <ValidationMessage>{personalDialog.error}</ValidationMessage>}
          </Field>
          <div className={classes.dialogActions}>
            <Button type="submit" disabled={isCreating}>
              {t('teamSettings.common.add')}
            </Button>
            <Button variant="tertiary" onClick={() => setPersonalDialog(null)}>
              {t('teamSettings.common.cancel')}
            </Button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={isSlackDialogOpen}
        onClose={closeSlackDialog}
        closedby="any"
        closeButton={false}
        aria-labelledby="slack-channel-heading"
        className={classes.dialog}
      >
        <div className={classes.dialogContent}>
          <Heading id="slack-channel-heading" level={2} data-size="xs">
            {t('integrations.slack.dialogTitle')}
          </Heading>
          <Paragraph>{t('integrations.slack.dialogDescription')}</Paragraph>
          <Field>
            <Label htmlFor="slack-workspace">{t('integrations.slack.workspace')}</Label>
            <Select
              id="slack-workspace"
              value={selectedSlackWorkspace?.toString() ?? ''}
              onChange={(e) => {
                setSelectedSlackWorkspace(e.target.value ? Number(e.target.value) : null);
                setSelectedSlackChannel('');
              }}
            >
              <Select.Option value="">{t('integrations.slack.workspacePlaceholder')}</Select.Option>
              {workspaces.map((workspace) => (
                <Select.Option key={workspace.id} value={workspace.id.toString()}>
                  {workspace.workspaceName}
                </Select.Option>
              ))}
            </Select>
          </Field>
          {selectedSlackWorkspace && (
            <Field>
              <Label htmlFor="slack-channel">{t('integrations.slack.channel')}</Label>
              <Select
                id="slack-channel"
                value={selectedSlackChannel}
                onChange={(e) => setSelectedSlackChannel(e.target.value)}
              >
                <Select.Option value="">{t('integrations.slack.channelPlaceholder')}</Select.Option>
                {slackChannelOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </Field>
          )}
          <div className={classes.dialogActions}>
            <Button
              onClick={handleAddSlackChannel}
              disabled={!selectedSlackChannel || !selectedSlackWorkspace || isInstallingSlackChannel}
            >
              {t('teamSettings.common.add')}
            </Button>
            <Button variant="tertiary" onClick={closeSlackDialog}>
              {t('teamSettings.common.cancel')}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={isWebhookDialogOpen}
        onClose={() => setIsWebhookDialogOpen(false)}
        closedby="any"
        closeButton={false}
        aria-labelledby="webhook-heading"
        className={classes.dialog}
      >
        <form className={classes.dialogContent} onSubmit={handleAddWebhook} noValidate>
          <Heading id="webhook-heading" level={2} data-size="xs">
            {t('integrations.advanced.dialogTitle')}
          </Heading>
          <Field>
            <Label htmlFor="webhook-type">{t('teamSettings.channels.webhookType')}</Label>
            <Select
              id="webhook-type"
              value={webhookType}
              onChange={(e) => setWebhookType(e.target.value as WebhookType)}
            >
              <Select.Option value="Slack">Slack</Select.Option>
              <Select.Option value="Microsoft Teams">Microsoft Teams</Select.Option>
            </Select>
          </Field>
          <Field>
            <Label htmlFor="webhook-url">{t('teamSettings.channels.webhookUrl')}</Label>
            <Input
              id="webhook-url"
              type="url"
              inputMode="url"
              value={webhookValue}
              placeholder={
                webhookType === 'Slack'
                  ? 'https://hooks.slack.com/services/...'
                  : 'https://outlook.office.com/webhook/...'
              }
              onChange={(e) => setWebhookValue(e.target.value)}
            />
          </Field>
          <Alert data-color="warning">
            <Paragraph data-size="sm">{t('integrations.advanced.legacyWarning')}</Paragraph>
          </Alert>
          <div className={classes.dialogActions}>
            <Button type="submit" disabled={!webhookValue.trim() || isCreating}>
              {t('teamSettings.common.add')}
            </Button>
            <Button variant="tertiary" onClick={() => setIsWebhookDialogOpen(false)}>
              {t('teamSettings.common.cancel')}
            </Button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={channelToDelete !== null}
        onClose={() => setChannelToDelete(null)}
        closedby="any"
        closeButton={false}
        aria-labelledby="remove-channel-heading"
        className={classes.dialog}
      >
        <div className={classes.dialogContent}>
          <Heading id="remove-channel-heading" level={2} data-size="xs">
            {t('teamSettings.channels.removeTitle')}
          </Heading>
          <Paragraph>
            {t('teamSettings.channels.removeDescription', {
              value: channelToDelete ? displayValue(channelToDelete) : '',
            })}
          </Paragraph>
          <div className={classes.dialogActions}>
            <Button data-color="danger" onClick={onDeleteDistributionChannel} disabled={isDeleting}>
              {t('teamSettings.common.remove')}
            </Button>
            <Button variant="tertiary" onClick={() => setChannelToDelete(null)}>
              {t('teamSettings.common.cancel')}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};
